/**
 * ErrorBoundary 组件
 * 捕获子组件渲染过程中的 JavaScript 错误，防止整个应用崩溃
 * 符合 React 错误边界规范
 */

import React, { Component, type ReactNode, type ErrorInfo } from 'react';
import Icon from './icon/Icon';
import Button from './bootstrap/Button';

// ============ 类型定义 ============

interface ErrorBoundaryProps {
  children: ReactNode;
  /** 自定义 fallback UI */
  fallback?: ReactNode;
  /** 错误回调 */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// ============ 组件实现 ============

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * 静态方法：从错误中派生状态
   * 在渲染阶段调用，用于更新 state 使下次渲染显示 fallback UI
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  /**
   * 生命周期方法：捕获错误信息
   * 在提交阶段调用，可用于日志上报
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // 调用外部错误回调（可用于上报错误监控系统）
    this.props.onError?.(error, errorInfo);
  }

  /**
   * 重置错误状态
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * 刷新页面
   */
  handleRefresh = (): void => {
    window.location.reload();
  };

  /**
   * 返回首页
   */
  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // 如果提供了自定义 fallback，使用自定义的
      if (fallback) {
        return fallback;
      }

      // 默认错误 UI
      return (
        <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
          <div className="text-center p-5" style={{ maxWidth: '500px' }}>
            {/* 错误图标 */}
            <div className="mb-4">
              <Icon
                icon="ErrorOutline"
                size="5x"
                color="danger"
                style={{ opacity: 0.8 }}
              />
            </div>

            {/* 错误标题 */}
            <h2 className="h3 mb-3 text-dark">页面出错了</h2>

            {/* 错误描述 */}
            <p className="text-muted mb-4">
              抱歉，页面渲染时遇到了问题。
              <br />
              您可以尝试刷新页面或返回首页。
            </p>

            {/* 错误详情（开发环境显示） */}
            {process.env.NODE_ENV === 'development' && error && (
              <div className="alert alert-light text-start mb-4" style={{ fontSize: '0.875rem' }}>
                <strong className="text-danger">Error:</strong>
                <code className="d-block mt-2 text-break" style={{ whiteSpace: 'pre-wrap' }}>
                  {error.message}
                </code>
              </div>
            )}

            {/* 操作按钮 */}
            <div className="d-flex gap-3 justify-content-center">
              <Button
                color="primary"
                icon="Refresh"
                onClick={this.handleRefresh}
              >
                刷新页面
              </Button>
              <Button
                color="light"
                icon="Home"
                onClick={this.handleGoHome}
              >
                返回首页
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
